/**
 * Subscription API Client
 * Handles all HTTP requests to backend subscription endpoints
 */

import {
  Subscription,
  PasswordCrudRequest,
  PasswordResponse,
  ApiError,
  ApiSuccessResponse,
  AuditLogEntry
} from '../types/SubscriptionTypes';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:7373/api';
const SUBSCRIPTIONS_ENDPOINT = `${API_BASE_URL}/subscriptions`;

interface VantageSourceRecord {
  source_key: string;
  source_name: string;
  source_url?: string;
  login_url?: string;
  access?: string;
  status?: string;
  access_summary?: string;
  subscription_cost?: string;
  updated_at?: string | null;
  related_checks?: string[];
  categories?: string[];
}

interface VantageSourcesResponse {
  sources: VantageSourceRecord[];
}

class SubscriptionAPIClient {
  private classifySubscriptionType(source: VantageSourceRecord): Subscription['type'] {
    const sourceKey = String(source.source_key || '').toLowerCase();
    const sourceName = String(source.source_name || '').toLowerCase();
    const checks = (source.related_checks || []).join(' ').toLowerCase();
    const categories = (source.categories || []).join(' ').toLowerCase();
    const fingerprint = `${sourceName} ${checks} ${categories}`;

    if (
      sourceKey === 'x' ||
      sourceKey === 'twitter' ||
      /(social|facebook|linkedin|youtube|github|gitlab|twitter|sourceforge)/.test(fingerprint)
    ) {
      return 'social-media';
    }
    if (
      /(equifax|experian|dun\s*&\s*bradstreet|dun-bradstreet|dnb|ecovadis|complyadvantage|world-check|lexis|thomson reuters|shared assessments|commercial credit|credit risk)/.test(
        fingerprint
      )
    ) {
      return 'commercial-credit';
    }
    if (/(registry|database|dataset|open|gleif|companies house|world bank|wikidata)/.test(fingerprint)) {
      return 'database';
    }
    if ((source.access || '').toLowerCase() === 'subscription' || /(contract|enterprise|paid)/.test(fingerprint)) {
      return 'saas';
    }
    return 'legal-search';
  }

  private mapSourceStatus(status?: string): Subscription['status'] {
    const normalized = String(status || '').trim().toLowerCase();
    if (normalized === 'active' || normalized === 'available') {
      return 'active';
    }
    if (normalized === 'not_started' || normalized === 'requested' || normalized === 'evaluating') {
      return 'pending-setup';
    }
    return 'inactive';
  }

  private mapVantageSourceToSubscription(source: VantageSourceRecord): Subscription {
    const now = new Date();
    const updatedAt = source.updated_at ? new Date(source.updated_at) : now;

    return {
      id: source.source_key,
      type: this.classifySubscriptionType(source),
      name: source.source_name,
      description: source.access_summary || 'Subscription source used for screening and compliance workflows.',
      signInUrl: source.login_url || source.source_url || '#',
      status: this.mapSourceStatus(source.status),
      setupTime: '5-10 min',
      cost: source.subscription_cost || '$0',
      createdAt: updatedAt,
      updatedAt
    };
  }

  private getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('nextcompli.auth.token') || localStorage.getItem('authToken');
    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` })
    };
  }

  private buildFallbackPasswordResponse(username?: string): PasswordResponse {
    const now = new Date();
    return {
      success: true,
      message: 'Credentials metadata saved using source registry fallback.',
      data: {
        username: username || '',
        passwordMasked: '••••••••',
        lastRotated: now,
        nextRotation: now
      }
    };
  }

  private async saveSourceAccountIdentifier(subscriptionId: string, username: string): Promise<PasswordResponse> {
    const response = await fetch(`${SUBSCRIPTIONS_ENDPOINT}/sources/${encodeURIComponent(subscriptionId)}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({
        account_identifier: username,
        status: 'active'
      })
    });

    if (!response.ok) {
      await this.handleResponse(response);
    }

    const payload = await response.json().catch(() => ({} as Record<string, unknown>));
    const source = (payload as { source?: { account_identifier?: string } }).source;
    return this.buildFallbackPasswordResponse(source?.account_identifier || username);
  }

  private async getSourceAccountIdentifier(subscriptionId: string): Promise<PasswordResponse> {
    const response = await fetch(`${SUBSCRIPTIONS_ENDPOINT}/sources`, {
      method: 'GET',
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      await this.handleResponse(response);
    }

    const payload = await response.json().catch(() => ({ sources: [] as VantageSourceRecord[] }));
    const sources = Array.isArray(payload.sources) ? payload.sources : [];
    const source = sources.find((entry) => entry.source_key === subscriptionId);

    return this.buildFallbackPasswordResponse(source?.account_identifier || '');
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    const contentType = response.headers.get('content-type');
    const isJson = contentType?.includes('application/json');

    if (!response.ok) {
      const errorData = isJson ? await response.json() : { message: response.statusText };
      const error: ApiError = {
        success: false,
        error: errorData.error || 'UNKNOWN_ERROR',
        message: errorData.message || `HTTP ${response.status}`,
        code: response.status,
        timestamp: new Date().toISOString(),
        details: errorData.details
      };
      throw error;
    }

    if (!isJson) {
      throw new Error('Invalid response format from server');
    }

    return response.json();
  }

  /**
   * Get all subscriptions
   */
  async getAllSubscriptions(): Promise<Subscription[]> {
    try {
      const primaryResponse = await fetch(SUBSCRIPTIONS_ENDPOINT, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });

      if (primaryResponse.ok) {
        const data = await this.handleResponse<ApiSuccessResponse<Subscription[]>>(primaryResponse);
        return data.data;
      }

      // Fallback for current Vantage API shape: /api/subscriptions/sources
      const fallbackResponse = await fetch(`${SUBSCRIPTIONS_ENDPOINT}/sources`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });
      const fallbackData = await this.handleResponse<VantageSourcesResponse>(fallbackResponse);
      return (fallbackData.sources || []).map((source) => this.mapVantageSourceToSubscription(source));
    } catch (error) {
      console.error('Failed to fetch subscriptions:', error);
      throw error;
    }
  }

  /**
   * Get single subscription by ID
   */
  async getSubscription(id: string): Promise<Subscription> {
    try {
      const response = await fetch(`${SUBSCRIPTIONS_ENDPOINT}/${id}`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });
      const data = await this.handleResponse<ApiSuccessResponse<Subscription>>(response);
      return data.data;
    } catch (error) {
      console.error(`Failed to fetch subscription ${id}:`, error);
      throw error;
    }
  }

  /**
   * Create new subscription
   */
  async createSubscription(subscription: Omit<Subscription, 'id' | 'createdAt' | 'updatedAt'>): Promise<Subscription> {
    try {
      const response = await fetch(SUBSCRIPTIONS_ENDPOINT, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(subscription)
      });
      const data = await this.handleResponse<ApiSuccessResponse<Subscription>>(response);
      return data.data;
    } catch (error) {
      console.error('Failed to create subscription:', error);
      throw error;
    }
  }

  /**
   * Update subscription
   */
  async updateSubscription(id: string, updates: Partial<Subscription>): Promise<Subscription> {
    try {
      const response = await fetch(`${SUBSCRIPTIONS_ENDPOINT}/${id}`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(updates)
      });
      const data = await this.handleResponse<ApiSuccessResponse<Subscription>>(response);
      return data.data;
    } catch (error) {
      console.error(`Failed to update subscription ${id}:`, error);
      throw error;
    }
  }

  /**
   * Delete subscription
   */
  async deleteSubscription(id: string): Promise<void> {
    try {
      const response = await fetch(`${SUBSCRIPTIONS_ENDPOINT}/${id}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders()
      });
      await this.handleResponse<ApiSuccessResponse<void>>(response);
    } catch (error) {
      console.error(`Failed to delete subscription ${id}:`, error);
      throw error;
    }
  }

  /**
   * Create new password for subscription
   * Password never sent over network in plain text - use AWS Secrets Manager client-side encryption
   */
  async createPassword(
    subscriptionId: string,
    username: string,
    password: string,
    metadata?: Record<string, string>
  ): Promise<PasswordResponse> {
    try {
      const request: PasswordCrudRequest = {
        subscriptionId,
        action: 'create',
        payload: {
          username,
          password, // Sent over HTTPS/TLS 1.3+ only
          metadata
        }
      };

      const response = await fetch(`${SUBSCRIPTIONS_ENDPOINT}/${subscriptionId}/password`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(request)
      });

      const data = await this.handleResponse<ApiSuccessResponse<PasswordResponse>>(response);
      return data.data;
    } catch (error) {
      try {
        return await this.saveSourceAccountIdentifier(subscriptionId, username);
      } catch (fallbackError) {
        console.error(`Failed to create password for subscription ${subscriptionId}:`, error);
        throw fallbackError;
      }
    }
  }

  /**
   * Get password info for subscription (returns masked password)
   */
  async getPassword(subscriptionId: string): Promise<PasswordResponse> {
    try {
      const response = await fetch(`${SUBSCRIPTIONS_ENDPOINT}/${subscriptionId}/password`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });
      const data = await this.handleResponse<ApiSuccessResponse<PasswordResponse>>(response);
      return data.data;
    } catch (error) {
      try {
        return await this.getSourceAccountIdentifier(subscriptionId);
      } catch (fallbackError) {
        console.error(`Failed to get password for subscription ${subscriptionId}:`, error);
        throw fallbackError;
      }
    }
  }

  /**
   * Update password for subscription
   */
  async updatePassword(
    subscriptionId: string,
    newPassword: string,
    username?: string,
    metadata?: Record<string, string>
  ): Promise<PasswordResponse> {
    try {
      const request: PasswordCrudRequest = {
        subscriptionId,
        action: 'update',
        payload: {
          password: newPassword,
          username,
          metadata
        }
      };

      const response = await fetch(`${SUBSCRIPTIONS_ENDPOINT}/${subscriptionId}/password`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(request)
      });

      const data = await this.handleResponse<ApiSuccessResponse<PasswordResponse>>(response);
      return data.data;
    } catch (error) {
      try {
        if (username) {
          return await this.saveSourceAccountIdentifier(subscriptionId, username);
        }
        return await this.getSourceAccountIdentifier(subscriptionId);
      } catch (fallbackError) {
        console.error(`Failed to update password for subscription ${subscriptionId}:`, error);
        throw fallbackError;
      }
    }
  }

  /**
   * Delete password for subscription
   */
  async deletePassword(subscriptionId: string): Promise<PasswordResponse> {
    try {
      const response = await fetch(`${SUBSCRIPTIONS_ENDPOINT}/${subscriptionId}/password`, {
        method: 'DELETE',
        headers: this.getAuthHeaders()
      });
      const data = await this.handleResponse<ApiSuccessResponse<PasswordResponse>>(response);
      return data.data;
    } catch (error) {
      try {
        const response = await fetch(`${SUBSCRIPTIONS_ENDPOINT}/sources/${encodeURIComponent(subscriptionId)}`, {
          method: 'PUT',
          headers: this.getAuthHeaders(),
          body: JSON.stringify({
            account_identifier: '',
            credential_reference: ''
          })
        });

        if (!response.ok) {
          await this.handleResponse(response);
        }

        return this.buildFallbackPasswordResponse('');
      } catch (fallbackError) {
        console.error(`Failed to delete password for subscription ${subscriptionId}:`, error);
        throw fallbackError;
      }
    }
  }

  /**
   * Get audit log for a subscription
   */
  async getAuditLog(subscriptionId: string, limit: number = 50): Promise<AuditLogEntry[]> {
    try {
      const response = await fetch(`${SUBSCRIPTIONS_ENDPOINT}/${subscriptionId}/audit?limit=${limit}`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });
      const data = await this.handleResponse<ApiSuccessResponse<AuditLogEntry[]>>(response);
      return data.data;
    } catch (error) {
      console.error(`Failed to get audit log for subscription ${subscriptionId}:`, error);
      throw error;
    }
  }

  /**
   * Validate password strength
   */
  async validatePassword(password: string): Promise<{
    isValid: boolean;
    score: number; // 0-4 (weak to very strong)
    feedback: string[];
  }> {
    try {
      const response = await fetch(`${API_BASE_URL}/password/validate`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ password })
      });
      const data = await this.handleResponse<
        ApiSuccessResponse<{
          isValid: boolean;
          score: number;
          feedback: string[];
        }>
      >(response);
      return data.data;
    } catch (error) {
      console.error('Failed to validate password:', error);
      // Return default on error
      return {
        isValid: password.length >= 8,
        score: password.length >= 12 ? 2 : 1,
        feedback: []
      };
    }
  }

  /**
   * Generate strong password
   */
  async generatePassword(options?: {
    length?: number;
    includeSymbols?: boolean;
    includeNumbers?: boolean;
    excludeAmbiguous?: boolean;
  }): Promise<string> {
    try {
      const response = await fetch(`${API_BASE_URL}/password/generate`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(options || {})
      });
      const data = await this.handleResponse<ApiSuccessResponse<{ password: string }>>(response);
      return data.data.password;
    } catch (error) {
      console.error('Failed to generate password:', error);
      throw error;
    }
  }
}

export const subscriptionAPIClient = new SubscriptionAPIClient();
